import React, { useState } from 'react';
import { SlideDown } from 'react-slidedown';

import useStoreTranslation from 'hooks/useStoreTranslation';
import { AirFareConditions } from 'models/flightValidation';

import { Card, CardContent, CardContentContainer } from './styles';

interface Props {
  policies: AirFareConditions[];
}

const CancellationPolicies = ({ policies }: Props) => {
  const { t } = useStoreTranslation();

  const [showPolicies, setShowPolicies] = useState(false);

  const toggleShowPolicies = () => setShowPolicies(!showPolicies);

  const buildPenaltyText = (
    allowed: boolean,
    allowedAfter: boolean,
    amount?: { currencyCode: string; fare: number; taxes: number },
    amountAfter?: { currencyCode: string; fare: number; taxes: number },
    isChange?: boolean
  ) => {
    const buildDetail = (
      allowedValue: boolean,
      amountValue?: { currencyCode: string; fare: number; taxes: number }
    ) => {
      
      if (!allowedValue)
        return t('flightDetails.cancellationPolicies.change.notAllowed');

      const total = (amountValue?.fare || 0) + (amountValue?.taxes || 0);
      let penaltyText = '';

      if (total > 0) {
        isChange
          ? penaltyText = t('flightDetails.cancellationPolicies.change.changePenalty', {
              currency: amountValue?.currencyCode,
              amount: total,
            })
          : penaltyText = t('flightDetails.cancellationPolicies.cancellation.cancelPenalty', {
              currency: amountValue?.currencyCode,
              amount: total,
            });
      }

      if (isChange) {
        if (!allowed && !allowedAfter)
          return t('flightDetails.cancellationPolicies.change.notAllowed');
        if ((allowed || allowedAfter) && (amount && amount.fare + amount.taxes > 0) || (amountAfter && amountAfter.fare + amountAfter.taxes > 0))
          return t('flightDetails.cancellationPolicies.change.allowedWithPenalty') + (penaltyText ? ` ${penaltyText}` : '');
        if ((allowed || allowedAfter) && (amount && amount.fare + amount.taxes === 0) && (amountAfter && amountAfter.fare + amountAfter.taxes === 0))
          return t('flightDetails.cancellationPolicies.change.allowedWithoutPenalty');

        return t('flightDetails.cancellationPolicies.change.notAllowed');
      }
      else {
        if (!allowed && !allowedAfter)
          return t('flightDetails.cancellationPolicies.cancellation.notAllowed');
        if ((allowed || allowedAfter) && (amount && amount.fare + amount.taxes > 0) || (amountAfter && amountAfter.fare + amountAfter.taxes > 0))
          return t('flightDetails.cancellationPolicies.cancellation.allowedWithPenalty') + (penaltyText ? ` ${penaltyText}` : '');
        if ((allowed || allowedAfter) && (amount && amount.fare + amount.taxes === 0) && (amountAfter && amountAfter.fare + amountAfter.taxes === 0))
          return t('flightDetails.cancellationPolicies.cancellation.allowedWithoutPenalty');

        return t('flightDetails.cancellationPolicies.cancellation.notAllowed');
      }
    };

    const beforeDetail = buildDetail(allowed, amount);
    const afterDetail = buildDetail(allowedAfter, amountAfter);

    /* compara allowed + amount */
    const sameConditions =
      (allowed === allowedAfter &&
      ((amount?.fare || 0) + (amount?.taxes || 0) === (amountAfter?.fare || 0) + (amountAfter?.taxes || 0)));

    if (sameConditions) {
      return beforeDetail;
    }

    return `${t(
        'flightDetails.before'
      )} ${beforeDetail}, ${t(
        'flightDetails.after'
      )} ${afterDetail}`;
  };

  return (
    <Card>
      <Card.Row onClick={toggleShowPolicies}>
        <Card.Title>{t('flightDetails.cancellationPolicies.title')}</Card.Title>
        <Card.Arrow className={showPolicies ? 'active' : ''} />
      </Card.Row>
      <SlideDown>
        {showPolicies && (
          <CardContentContainer>
            {policies.map((policy, index) => (
              <CardContent key={index}>
                <CardContent.FlightLabel>
                  {`${t('flightDetails.cancellationPolicies.flight')} ${policy.flight}`}
                </CardContent.FlightLabel>
                <CardContent.PolicyRow>
                  <CardContent.FlightPolicy>{`${t(
                    'flightDetails.cancellationPolicies.change.title',
                  )}: `}</CardContent.FlightPolicy>
                  <CardContent.FlightPolicyValue>
                    {buildPenaltyText(policy.changesAllowed, policy.changesAllowedAfter, policy.changesAmount, policy.changesAmountAfter, true)}
                  </CardContent.FlightPolicyValue>
                </CardContent.PolicyRow>
                <CardContent.PolicyRow>
                  <CardContent.FlightPolicy>{`${t(
                    'flightDetails.cancellationPolicies.cancellation.title',
                  )}: `}</CardContent.FlightPolicy>
                  <CardContent.FlightPolicyValue>
                    {buildPenaltyText(policy.cancellationAllowed, policy.cancellationAllowedAfter, policy.cancellationAmount, policy.cancellationAmountAfter, false)}                    
                  </CardContent.FlightPolicyValue>
                </CardContent.PolicyRow>
                <CardContent.PolicyRow>
                  <CardContent.FlightPolicy>{`${t(
                    'flightDetails.cancellationPolicies.noShow.title',
                  )}: `}</CardContent.FlightPolicy>
                  <CardContent.FlightPolicyValue>
                    <>
                      {`${t('flightDetails.cancellationPolicies.noShow.beginText')} 
                      ${
                        policy.noShow === 0
                          ? t('flightDetails.cancellationPolicies.noShow.noshow-0')
                          : policy.noShow === 1
                          ? t('flightDetails.cancellationPolicies.noShow.noshow-1')
                          : policy.noShow === 2
                          ? t('flightDetails.cancellationPolicies.noShow.noshow-2')
                          : policy.noShow === 3
                          ? t('flightDetails.cancellationPolicies.noShow.noshow-3')
                          : ''
                      }. ${t('flightDetails.cancellationPolicies.noShow.endText')}`}
                    </>
                  </CardContent.FlightPolicyValue>
                </CardContent.PolicyRow>
              </CardContent>
            ))}
            <CardContent.Disclaimer>{t('flightDetails.cancellationPolicies.disclaimer')}</CardContent.Disclaimer>
          </CardContentContainer>
        )}
      </SlideDown>
    </Card>
  );
};

export default CancellationPolicies;
